"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";

import ptTranslations from "@/locales/pt.json";
import enTranslations from "@/locales/en.json";
import esTranslations from "@/locales/es.json";

type Locale = "pt" | "en" | "es";

type TranslationData = typeof ptTranslations;

const translations: Record<Locale, TranslationData> = {
  pt: ptTranslations,
  en: enTranslations,
  es: esTranslations,
};

interface TranslationContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const STORAGE_KEY = "mach9_locale";

export function TranslationProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("pt");
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const savedLocale = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (savedLocale && translations[savedLocale]) {
      setLocaleState(savedLocale);
    }
    setIsLoaded(true);
  }, []);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem(STORAGE_KEY, newLocale);
  };

  const t = (key: string, vars?: Record<string, string | number>): string => {
    const keys = key.split(".");
    let value: unknown = translations[locale];

    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        return key;
      }
    }

    if (typeof value !== "string") return key;

    // Interpolação de variáveis: substitui {nome} pelos valores em `vars`.
    if (!vars) return value;
    return value.replace(/\{(\w+)\}/g, (match, name) => {
      return name in vars ? String(vars[name]) : match;
    });
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <TranslationContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
}
